import * as React from "react"
import { useQuery } from "@tanstack/react-query"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { api } from "@/lib/api"
import { format } from "date-fns"
import {
  Zap,
  RefreshCw,
  Filter,
  Search,
  Play,
  Pause,
  ChevronRight,
  Activity,
  ShoppingCart,
  CreditCard,
  Users,
  Server,
  Bell,
} from "lucide-react"

function EventExplorer() {
  const [filter, setFilter] = React.useState("")
  const [isLive, setIsLive] = React.useState(true)

  const { data: events, isLoading, refetch } = useQuery({
    queryKey: ["events", filter],
    queryFn: () => api.get(`/events?${filter ? `event=${filter}` : ""}`),
    refetchInterval: isLive ? 2000 : false,
  })

  const { data: stats } = useQuery({
    queryKey: ["event-stats"],
    queryFn: () => api.get("/events/stats"),
    refetchInterval: 5000,
  })

  const eventIcons = {
    "user.created": <Users className="h-4 w-4" />,
    "user.updated": <Users className="h-4 w-4" />,
    "order.created": <ShoppingCart className="h-4 w-4" />,
    "order.completed": <ShoppingCart className="h-4 w-4" />,
    "payment.created": <CreditCard className="h-4 w-4" />,
    "payment.confirmed": <CreditCard className="h-4 w-4" />,
    "panel.offline": <Server className="h-4 w-4" />,
    "panel.online": <Server className="h-4 w-4" />,
    "scan.completed": <Activity className="h-4 w-4" />,
    "backup.created": <Bell className="h-4 w-4" />,
  }

  const eventColors = {
    "user.created": "info",
    "user.updated": "info",
    "order.created": "success",
    "order.completed": "success",
    "payment.created": "warning",
    "payment.confirmed": "success",
    "panel.offline": "destructive",
    "panel.online": "success",
    "scan.completed": "info",
    "backup.created": "success",
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Event Explorer</h1>
          <p className="text-muted-foreground">Real-time event monitoring and filtering</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={isLive ? "default" : "outline"}
            onClick={() => setIsLive(!isLive)}
          >
            {isLive ? <Pause className="mr-2 h-4 w-4" /> : <Play className="mr-2 h-4 w-4" />}
            {isLive ? "Live" : "Paused"}
          </Button>
          <Button onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats?.data && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Today</CardTitle>
              <Zap className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.data.today}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">This Week</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.data.thisWeek}</div>
            </CardContent>
          </Card>
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="text-sm font-medium">Top Events</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {stats.data.byEvent?.slice(0, 5).map((e) => (
                  <Badge key={e.event} variant="outline">
                    {e.event}: {e.count}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Filter events..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="max-w-sm"
        />
      </div>

      {/* Event Stream */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Live Event Stream
            {isLive && (
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-success"></span>
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[500px]">
            <div className="space-y-2">
              {events?.data?.map((event) => (
                <div
                  key={event.id}
                  className="flex items-start gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="mt-1">
                    {eventIcons[event.event] || <Zap className="h-4 w-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge variant={eventColors[event.event] || "secondary"}>
                        {event.event}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        {format(new Date(event.created_at * 1000), "HH:mm:ss")}
                      </span>
                    </div>
                    <p className="text-sm mt-1 truncate">
                      {JSON.stringify(event.data)}
                    </p>
                    {event.source && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Source: {event.source}
                      </p>
                    )}
                  </div>
                  <Button size="sm" variant="ghost">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Event Flow */}
      <Card>
        <CardHeader>
          <CardTitle>Event Flow</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between overflow-x-auto py-4">
            {["User Action", "Event Bus", "Handlers", "Side Effects", "Audit Log"].map(
              (step, index) => (
                <React.Fragment key={step}>
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-2xl">{["👤", "⚡", "🔧", "📋", "📝"][index]}</span>
                    </div>
                    <span className="text-sm mt-2 text-center">{step}</span>
                  </div>
                  {index < 4 && <ChevronRight className="h-6 w-6 text-muted-foreground mx-2" />}
                </React.Fragment>
              )
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default EventExplorer
